import React, { useEffect } from 'react';
import { useParams, Navigate } from 'react-router-dom';
import { useSession } from 'session-context';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import BonusTracker from 'bonus-tracker-component';
import WeatherWindGenerator from 'weather-wind-generator';
import NavalEncounterGenerator from 'naval-encounter-generator';
import SeafaringTurnTracker from 'seafaring-turn-tracker';
import InteractiveShipHull from 'interactive-ship-hull';
import CAPTracker from './cap-tracker-component';
import RulesReference from './rules-reference-component';
import { AlertTriangle, Ship, AnchorIcon, Wind, Shield, Radar, Book } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';

const CampaignDashboard: React.FC = () => {
  const { 
    sessionId: contextSessionId, 
    gameState, 
    userId,
    joinSession, 
    updateGameState,
    updateUserActivity,
    updateSharedDaysInTurn,
    isDM
  } = useSession();
  const { sessionId } = useParams<{ sessionId: string }>();
  
  // If we have a sessionId from the URL but it doesn't match our context, try to join that session
  useEffect(() => {
    if (sessionId && sessionId !== contextSessionId) {
      joinSession(sessionId, false);
    }
  }, [sessionId, contextSessionId, joinSession]);
  
  // If we have no game state or the sessionIds don't match, show loading or redirect
  if (!gameState || (sessionId && sessionId !== contextSessionId)) {
    return <div className="text-center p-8">Loading session data...</div>;
  }
  
  // If no sessionId in URL, redirect to home
  if (!sessionId) {
    return <Navigate to="/" replace />;
  }
  
  // Handler functions for updating game state
  const handleMoraleChange = (moraleState: typeof gameState.moraleState) => {
    updateGameState('moraleState', moraleState);
  };
  
  const handleCleanlinessChange = (cleanlinessState: typeof gameState.cleanlinessState) => {
    updateGameState('cleanlinessState', cleanlinessState);
  };
  
  const handleNavigationChange = (navigationState: typeof gameState.navigationState) => {
    updateGameState('navigationState', navigationState);
  };
  
  const handleWeatherGenerated = (weatherData: typeof gameState.weatherData) => {
    updateGameState('weatherData', weatherData);
  };
  
  const handleUpperDeckUpdate = (upperDeckSections: typeof gameState.upperDeckSections) => {
    updateGameState('upperDeckSections', upperDeckSections);
  };
  
  const handleBottomDeckUpdate = (bottomDeckSections: typeof gameState.bottomDeckSections) => {
    updateGameState('bottomDeckSections', bottomDeckSections);
  };
  
  const handleRiggingUpdate = (riggingSections: typeof gameState.riggingSections) => {
    updateGameState('riggingSections', riggingSections);
  };
  
  const handleUserActivityChange = (uid: string, activityData: any) => {
    updateUserActivity(uid, activityData);
  };
  
  const handleSharedDaysInTurnChange = (days: number) => {
    updateSharedDaysInTurn(days);
  };
  
  const handleBonusesUpdate = (bonuses: typeof gameState.bonuses) => {
    updateGameState('bonuses', bonuses);
  };
  
  const handleRoleVacanciesUpdate = (roleVacancies: typeof gameState.roleVacancies) => {
    updateGameState('roleVacancies', roleVacancies);
  };

  // Check if user is DM
  const userIsDM = isDM();

  // Create a placeholder component for non-DM users
  const DMToolsPlaceholder = () => (
    <Card className="w-full">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <AlertTriangle className="h-5 w-5 text-amber-500" />
          DM Tools Access
        </CardTitle>
      </CardHeader>
      <CardContent>
        <p className="text-sm text-muted-foreground">
          Weather generation and encounter tools are only available to the Dungeon Master.
        </p>
      </CardContent>
    </Card>
  );
  
  return (
    <div className="container mx-auto p-4">
      <Tabs defaultValue="cruise" className="w-full">
        <TabsList className="grid grid-cols-5 mb-6">
          <TabsTrigger value="cruise" className="flex items-center gap-2">
            <Ship className="h-4 w-4" />
            Cruise Management
          </TabsTrigger>
          <TabsTrigger value="combat" className="flex items-center gap-2">
            <AnchorIcon className="h-4 w-4" />
            Ship Combat
          </TabsTrigger>
          <TabsTrigger value="dm" className="flex items-center gap-2">
            <Shield className="h-4 w-4" />
            DM Tools
          </TabsTrigger>
          <TabsTrigger value="bonuses" className="flex items-center gap-2">
            <Radar className="h-4 w-4" />
            Status Effects
          </TabsTrigger>
          <TabsTrigger value="rules" className="flex items-center gap-2">
            <Book className="h-4 w-4" />
            Rules Reference
          </TabsTrigger>
        </TabsList>

        {/* Cruise Management Tab */}
        <TabsContent value="cruise" className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <SeafaringTurnTracker 
              onMoraleChange={handleMoraleChange}
              onCleanlinessChange={handleCleanlinessChange}
              onNavigationChange={handleNavigationChange}
              currentMorale={gameState.moraleState}
              currentCleanliness={gameState.cleanlinessState}
              currentNavigation={gameState.navigationState}
            />
            <CAPTracker
              sharedDaysInTurn={gameState.sharedDaysInTurn || 3}
              userActivities={gameState.userActivities || {}}
              onUserActivityChange={handleUserActivityChange}
              onSharedDaysInTurnChange={handleSharedDaysInTurnChange}
            />
          </div>
        </TabsContent>

        {/* Ship Combat Tab */}
        <TabsContent value="combat">
          <InteractiveShipHull 
            upperDeckImage="https://i.imgur.com/wxTzrKd.jpeg"
            bottomDeckImage="https://i.imgur.com/L0c4qpV.jpeg"
            upperDeckSections={gameState.upperDeckSections}
            bottomDeckSections={gameState.bottomDeckSections}
            riggingSections={gameState.riggingSections}
            onUpperDeckUpdate={handleUpperDeckUpdate}
            onBottomDeckUpdate={handleBottomDeckUpdate}
            onRiggingUpdate={handleRiggingUpdate}
          />
        </TabsContent>

        {/* DM Tools Tab */}
        <TabsContent value="dm">
          {userIsDM ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <WeatherWindGenerator 
                onWeatherGenerated={handleWeatherGenerated}
                currentWeather={gameState.weatherData}
              />
              <NavalEncounterGenerator 
                weatherData={gameState.weatherData}
                crewMorale={gameState.moraleState}
                navigationState={gameState.navigationState}
                shipCleanliness={gameState.cleanlinessState}
              />
            </div>
          ) : (
            <DMToolsPlaceholder />
          )}
        </TabsContent>

        {/* Status Effects Tab */}
        <TabsContent value="bonuses">
          <BonusTracker 
            moraleState={gameState.moraleState} 
            cleanlinessState={gameState.cleanlinessState}
            bonuses={gameState.bonuses}
            roleVacancies={gameState.roleVacancies}
            onBonusesChange={handleBonusesUpdate}
            onRoleVacanciesChange={handleRoleVacanciesUpdate}
          />
        </TabsContent>

        {/* Rules Reference Tab */}
        <TabsContent value="rules">
          <RulesReference />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default CampaignDashboard;